import { Link, useNavigate } from "react-router-dom";
import { NavItem } from "@type/navItems";
import { LogOutIcon, SettingsIcon, ToggleLeftIcon, ToggleRightIcon, ArrowRightLeftIcon } from "lucide-react";
import { getCookie, removeCookie, setCookie } from "@util/cookie";
import { Fragment, useState } from "react";
import { useUserProfileContext } from "@context/user_profile";
import NotificationDot from "./notification-dot";
import { Tooltip } from "./tooltip";
import { getPostAuthLandingPath } from "@util/auth-redirect";

export interface ProductToggleConfig {
    activeLabel: string;
    otherLabel: string;
    otherPath: string;
    cookieValue: string;
}

interface Props {
    navItems: NavItem[];
    productToggle?: ProductToggleConfig;
}

export function MinimalSidebarV2({
    navItems,
    productToggle,
}: Props) {
    const navigate = useNavigate();
    const isAccountStartupScreening: boolean = Boolean((getCookie("__account_startup_screening__")));
    const [isLoadingUserInfo, currentUserInfo] = useUserProfileContext();
    const [showUserMenu, setShowUserMenu] = useState<boolean>(false);
    const [isShowDotResume, setShowDotResume] = useState<boolean>(!getCookie("__dot_resume_menu__"))
    const currentPath = window.location.pathname;
    const currentPage = navItems.find((item) => item.to === currentPath)?.name;

    const handleLogout = async () => {
        removeCookie("__apply_job_guest__")
        removeCookie("authToken");
        removeCookie("__account_startup_screening__");
        sessionStorage.removeItem("cv_uuid");
        sessionStorage.removeItem('__INVITE_MEMBER_EMAIL__');
        // redirect to login page
        window.location.href = "/";
    };

    return (
        <div className="fixed left-0 top-0 h-screen w-20 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 hidden sm:flex flex-col items-center py-6 z-10">
            <div className="mb-5">
                <Link to={getPostAuthLandingPath(currentUserInfo?.role)}>
                    <img width="50" src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_dashboard.png`} alt="ourteam" />
                </Link>
            </div>
            {!isLoadingUserInfo && currentUserInfo && (() => {
                // Items visible to this user, after role + context filters.
                const visibleItems = (navItems || []).filter((item) => {
                    if (item.roles.indexOf(currentUserInfo.role || "") === -1) return false;
                    if (isAccountStartupScreening && ["jobs", "hires", "assessments"].includes(item.name)) return false;
                    // hide payment menu for hdbank referral code
                    if (item.name === "payment" && import.meta.env.VITE_HDBANK_REFERRAL_CODE.split(",").includes(currentUserInfo.referral_code)) return false;
                    if (item.name === "hires" && currentUserInfo.permission === "member") return false;
                    return true;
                });

                // Group by `group` in first-appearance order; ungrouped navs fall
                // into one default group (so they render flat, unchanged).
                const groups: { key: string; items: NavItem[] }[] = [];
                for (const item of visibleItems) {
                    const key = item.group || "__default__";
                    let group = groups.find((g) => g.key === key);
                    if (!group) {
                        group = { key, items: [] };
                        groups.push(group);
                    }
                    group.items.push(item);
                }

                const renderItem = (item: NavItem) => {
                    const isActive = currentPage === item.name;
                    const Icon = item.icon;
                    return (
                        <button
                            key={item.name}
                            onClick={() => {
                                navigate(item.to as string)
                                if (item.label === "Resume") {
                                    setShowDotResume(false);
                                    setCookie("__dot_resume_menu__", "true");
                                }
                            }}
                            className={`relative flex w-full flex-col items-center justify-center gap-1.5 px-1`}
                        >
                            <Tooltip
                                content={item.tooltip || item.label}
                                side="right"
                                asChild
                            >
                                <div className={`rounded-lg p-2 transition-all duration-300 text-gray-500 hover:text-blue-600 ${isActive
                                    ? "bg-blue-50 text-blue-600 dark:bg-gray-800 dark:text-blue-400"
                                    : "hover:bg-blue-100 dark:hover:bg-gray-800"
                                    }`}>
                                    <Icon className={`hover:scale-110 transition-all duration-300 h-6 w-6 shrink-0 mx-auto ${isActive ? "text-blue-600 hover:text-blue-600 transition-all duration-300" : ""}`} />
                                </div>
                            </Tooltip>
                            <span className="text-[11px] text-center leading-tight">
                                {item.label}
                            </span>
                            {(isShowDotResume && item.label === "Resume") && (
                                <div className="absolute -top-[8px] -right-1">
                                    <NotificationDot size="small" />
                                </div>
                            )}
                        </button>
                    );
                };

                // The rail is `h-screen` and the item list is not: enough
                // products on one account and the last icons fall off the bottom
                // with no way to reach them. `flex-1` alone does not fix that —
                // a flex item will not shrink below its content until its own
                // overflow is something other than visible, which is why this
                // needs `overflow-y-auto` and not just a taller box. The avatar
                // block below keeps its `mt-auto` and stays pinned; only the
                // items move.
                //
                // No bar drawn: 80px of rail is too narrow to give a scrollbar a
                // column of its own. Same pairing the tab strips use.
                return (
                    <nav className="flex-1 overflow-y-auto scrollbar-hide flex flex-col items-center space-y-6 w-full">
                        {groups.map((group, gi) => (
                            <Fragment key={group.key}>
                                {/* thin divider between sections */}
                                {gi > 0 && <div className="w-8 border-t border-gray-200 dark:border-gray-800" />}
                                {group.items.map(renderItem)}
                            </Fragment>
                        ))}
                    </nav>
                );
            })()}

            <div className="mt-auto relative">
                {productToggle && (
                    <Tooltip
                        content={`Switch to ${productToggle.otherLabel}`}
                        side="right"
                        delayDuration={0}
                    >
                        <button
                            className="text-center w-full items-center justify-center flex flex-col mb-3 text-gray-500 hover:text-blue-600"
                            onClick={() => {
                                setCookie("__active_product__", productToggle.cookieValue);
                                window.location.href = productToggle.otherPath;
                            }}
                        >
                            <div className="rounded-lg p-2 hover:bg-blue-100 transition-colors">
                                <ArrowRightLeftIcon className="w-6 h-6" />
                            </div>
                            <span className="text-[10px] mt-0.5">{productToggle.otherLabel}</span>
                        </button>
                    </Tooltip>
                )}
                {currentUserInfo && ((currentUserInfo?.role === "hr" && import.meta.env.VITE_FIRSTMOVE_COMPANY_ID.split(",").includes(currentUserInfo?.company_id)) || currentUserInfo?.role === "super_admin") && (
                    <Tooltip
                        content={!isAccountStartupScreening ? "Switch to Startup Screening" : "Switch to Talent Pool"}
                        side="right"
                        delayDuration={0}
                    >
                        <button
                            className="text-center w-full items-center justify-center flex flex-row mb-3"
                            onClick={() => {
                                if (!isAccountStartupScreening) {
                                    setCookie("__account_startup_screening__", "true");
                                } else {
                                    removeCookie("__account_startup_screening__");
                                }
                                // window reload
                                window.location.reload();
                            }}
                        >
                            {isAccountStartupScreening ? (
                                <ToggleRightIcon className="w-7 h-7 text-gray-500" />
                            ) : (
                                <ToggleLeftIcon className="w-7 h-7 text-gray-500" />
                            )}
                        </button>
                    </Tooltip>
                )}
                <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden transition-colors border border-1 border-gray-200 bg-white"
                    aria-label="User menu"
                >
                    {!isLoadingUserInfo && <img
                        alt={currentUserInfo?.role}
                        onError={(e) => {
                            e.currentTarget.src = `${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/${currentUserInfo?.role === "admin" || currentUserInfo?.role === "super_admin" ? "super-admin-logo.png" : "default_avatar.png"}`;
                        }}
                        src={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/${currentUserInfo?.role !== "job_seeker" ? currentUserInfo?.company_logo : currentUserInfo?.profile_picture_path}`}
                    />}
                </button>

                {showUserMenu && (
                    <div className="absolute -bottom-3 left-28 -translate-x-1/2 w-36 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 border border-gray-200 dark:border-gray-700">
                        <button
                            onClick={() => navigate("/setting")}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            <SettingsIcon className="w-4 h-4 mr-2" />
                            Settings
                        </button>
                        <button
                            onClick={handleLogout}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            <LogOutIcon className="w-4 h-4 mr-2" />
                            Logout
                        </button>
                    </div>
                )}
            </div>
        </div >
    );
}
